import React, { useState, useRef, useEffect } from "react";
import classnames from "classnames";
import { fabric } from "sunzi-fabric";
import Zuploader from "zuploader";
import { ZuploaderImage } from "zuploader/types/zuploader";
import cloneDeep from "lodash.clonedeep";
import { Header, Loading, Wrapper, BlurTip, Clip } from "@/components";
import {
  i18n,
  myFaceCut,
  compressImage,
  faceRecognition,
  loadBase64FromURL,
  loadHTMLImage,
  upload7nB64,
  getToken7n,
  uploadKey,
  loadImageFromURL,
  thumbnail7n,
  loadSVGFromURL,
  base64ToBlob,
} from "@/utils";
import { useLocal } from "@/hooks";
import { Shop } from "@/sunzi";
import { Vip } from "@/increment";
import {
  Increment,
  VipIncrement,
  RelatedProductIncrement,
} from "@/increment/increment";
import {
  UserImage,
  ImageSort,
  ProductIncrement,
  MiniMeLineText,
  MiniMeData,
  CanvasData,
  FactoryData,
  ConfirmOption,
  MiniMeImage,
  AnnexData,
  ResponseAvatar,
  Color,
  ChristmasBag,
  ChristmasBadData,
  relatedRecordProductProps,
  NewTemplateArDatasProps,
  ElectricCard,
  SizeVariant,
  ColorVariant,
  ColorOption,
} from "./mini-me";
import Error from "./error";
import Choose from "./choose";
import Edit from "./edit";
import Icon from "./icon";
import Preview from "./preview";
import IncrementDrawer from "./increment-drawer";
import ImageDrawer from "./image-drawer";
import Share from "./share";
import ChooseAvatar from "./choose-avatar";
import RelatedProduct from "./related-product";
import styles from "./style.less";
import RelatedRecordProduct from "./related-record-product";
import RelatedCard from "./related-card";
import { faceDetect, thirdServiceUsed } from "@sunzi/utils";

const SaveLocalKey = "_sunzi_mini_me_local_image";

/** 任务状态 */
export enum TaskStatus {
  Uploading,
  NetworkError,
  CropError,
  ChooseAvatar,
  Choose,
  Preview,
}

/** 预览页面状态 */
export enum PreviewType {
  Image,
  Text,
}

/** 增值服务状态 */
export enum IncrementType {
  SameSide = -1,
  DoubleSide = 0,
  NewImageSide = 1,
  LineText = 2,
}

/** 拿到关键点坐标 */
export const getKeyPoint = (avatar: UserImage, feature: string) => {
  const { landmark } = avatar.faceRecognizeData!;
  if (feature === "head") {
    // 头
    const { leftEyebrow, rightEyebrow } = landmark;
    const keyPoint = {
      x: (leftEyebrow.x + rightEyebrow.x) / 2,
      y: leftEyebrow.y < rightEyebrow.y ? leftEyebrow.y : rightEyebrow.y,
    };
    return keyPoint;
  } else if (feature === "mouse") {
    // 嘴
    const { mouthLowerLip, mouthUpperLip } = landmark;
    const keyPoint = {
      x: (mouthLowerLip.x + mouthUpperLip.x) / 2,
      y: (mouthLowerLip.y + mouthUpperLip.y) / 2,
    };
    return keyPoint;
  } else {
    // 眼睛
    const { leftEyePupil, rightEyePupil } = landmark;
    const keyPoint = {
      x: (leftEyePupil.x + rightEyePupil.x) / 2,
      y: (leftEyePupil.y + rightEyePupil.y) / 2,
    };
    return keyPoint;
  }
};

/** 获取人脸识别信息 */
export const getFaceData = async (
  image: string,
): Promise<UserImage["faceRecognizeData"] | undefined> => {
  const imageData = await faceDetect(base64ToBlob(image));
  try {
    // @ts-ignore
    const { landmark, face_rectangle } = imageData[0];
    const chin = landmark.contour_chin;
    const leftEyebrow = landmark.left_eyebrow_upper_middle;
    const rightEyebrow = landmark.right_eyebrow_upper_middle;
    const leftEyePupil = landmark.left_eye_pupil;
    const rightEyePupil = landmark.right_eye_pupil;
    const nose = landmark.nose_tip;
    const mouthLowerLip = landmark.mouth_lower_lip_top;
    const mouthUpperLip = landmark.mouth_upper_lip_bottom;
    return {
      landmark: {
        chin,
        leftEyebrow,
        rightEyebrow,
        leftEyePupil,
        rightEyePupil,
        nose,
        mouthLowerLip,
        mouthUpperLip,
      },
      faceRect: face_rectangle,
    };
  } catch (error) {
    return undefined;
  } finally {
    thirdServiceUsed({
      type: 5,
      source: 4,
      internalSource: 3,
    });
  }
};

/** 保存本地图片数据 */
const saveLocalImage = (userImage: UserImage) => {
  const stringData = window.localStorage.getItem(SaveLocalKey);
  if (stringData) {
    // 如果本地有存，就在最前面加上
    const data = JSON.parse(stringData);
    // 遍历看看图片以前是否上传过
    for (let i = 0; i < data.length; i++) {
      const current = data[i].source;
      if (current === userImage.source) {
        return;
      }
    }
    data.unshift(userImage);
    // 最大 5MB，大于 4.5 MB 清除最后一个
    const size = JSON.stringify(data).length / 1024 / 1024;
    if (size > 4.5) {
      data.pop();
    }
    window.localStorage.setItem(SaveLocalKey, JSON.stringify(data));
  } else {
    // 如果本地没有存储，包成一个数组存起来
    const data = [userImage];
    window.localStorage.setItem(SaveLocalKey, JSON.stringify(data));
  }
};

const zuploaderI18n: any = {
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  fr: "fr_FR",
  jp: "ja_JP",
};

interface MiniMeProps {
  /** 店铺信息 */
  shop: Shop;
  /** 产品标题 */
  title: string;
  /** 价格 */
  price: string;
  /** 肤色 */
  colors: Color[];
  /** 默认选中参数 */
  defaultOption?: number;
  /** 已选择的肤色 */
  selectedSkinColor: string;
  /** 可刻面数 */
  sideCount: number;
  /** 加价 */
  productIncrement: ProductIncrement;
  /** 刻字相关数据 */
  lineText?: MiniMeLineText;
  /** 卡通人抱枕数据 */
  miniMeData: MiniMeData[];
  /** 变体数据 */
  sizeVariant: SizeVariant[];
  /** 追销录音器产品 */
  relatedRecordProduct?: relatedRecordProductProps;
  /** 增值服务 */
  increment?: Increment;
  /** 分享链接 */
  shareLink?: string;
  /** 分享文案 */
  shareText?: string;
  /** 分享优惠价格 */
  sharePrice?: number;
  /** 产品 */
  product?: string;
  /** 是否只有刻字 */
  isOnlyText?: boolean;
  /** 圣诞包数据 */
  christmasBag?: ChristmasBag;
  /** 电子卡数据 */
  electricCard?: ElectricCard;
  /** 颜色变体 */
  colorVariants?: ColorVariant[];
  /** 默认扣头图片 */
  defaultImage?: string;
  /** 指定模板id追销Ar动画，替代原tempalteId & tempalteIdRecordProduct */
  newTemplateArDatas?: NewTemplateArDatasProps[];
  /** 判断是否跳过追销Ar，替代原tempalteId */
  templateIdDoSkipAr?: boolean;
  /** 指定模板id（用于追销） */
  // templateId?: number | string;
  /** 模板id指定追销产品（ar动画替代钥匙扣数据） */
  // templateIdRecordProduct?: relatedRecordProductProps;
  /** 模板id指定追销产品（电子卡） */
  templateIdElectricCard?: ElectricCard;
  /** 模板id指定追销产品（礼盒） */
  templateIdChristmasBag?: ChristmasBag;
  /** 默认模板id */
  defaultTemplateId?: number | string;
  /** 谷歌分析 */
  gtag: any;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: (
    data: ConfirmOption,
    type: number,
    isShare: boolean,
    vipIncrement?: VipIncrement,
    relatedProductIncrement?: RelatedProductIncrement,
    christmasBag?: ChristmasBadData,
    relatedRecordProductIncrement?: relatedRecordProductProps,
    relatedCard?: any,
    colorVariantsValue?: ColorOption[],
  ) => void;
}

const MiniMe: React.FC<MiniMeProps> = ({
  shop,
  title,
  price,
  colors,
  defaultOption,
  selectedSkinColor,
  sideCount,
  productIncrement,
  lineText,
  miniMeData,
  sizeVariant,
  relatedRecordProduct,
  increment,
  shareLink,
  shareText,
  sharePrice,
  product,
  isOnlyText,
  christmasBag,
  electricCard,
  defaultImage,
  newTemplateArDatas,
  templateIdDoSkipAr,
  templateIdElectricCard,
  templateIdChristmasBag,
  colorVariants,
  defaultTemplateId,
  gtag,
  onClose,
  onConfirm,
}) => {
  // 多语言
  useLocal(shop.language);

  /** 第一次进入 */
  const _first = useRef<boolean>(true);
  /** clip 组件 */
  const _clip = useRef<Clip>(null);
  /** Choose 组件 */
  const _choose = useRef<any>();
  /** 当前处理的面数 */
  const currentSide = useRef<number>(1);
  /** 上一次处理的面数 */
  const prevSide = useRef<number>(currentSide.current);
  /** 加车数据 */
  const canvasData = useRef<CanvasData[]>([]);
  /** 增值服务状态 */
  const incrementType = useRef<IncrementType>();
  /** 工厂数据 */
  const factoryData = useRef<FactoryData>();
  /** 分享图片 */
  const shareBackgroundImage = useRef<string>("");
  /** 请求返回的头像列表 */
  const responseAvatar = useRef<ResponseAvatar[]>([]);
  /** 关联产品加车状态 */
  const relatedProductStatus = useRef<boolean>(false);
  /** zuploader 组件显影 */
  const [zuploaderVisible, setZuploaderVisible] = useState<boolean>(true);
  /** edit 组件显影 */
  const [editVisible, setEditVisible] = useState<boolean>(false);
  /** clip 组件显影 */
  const [clipVisible, setClipVisible] = useState<boolean>(false);
  /** 任务状态 */
  const [taskStatus, setTaskStatus] = useState<TaskStatus>(
    TaskStatus.Uploading,
  );
  /** 头像图片 */
  const [images, setImages] = useState<UserImage[]>([]);
  /** 裁剪状态 */
  const [cutState, setCutState] = useState<boolean>(false);
  /** 模糊提示组件显影 */
  const [blurTipVisible, setBlurTipVisible] = useState<boolean>(false);
  /** 分类选项 */
  const [sortOption, setSortOption] = useState<{
    [nameId: string]: ImageSort[];
  }>({});
  /** 带 path 的图片 */
  const [pathImage, setPathImage] = useState<{ [propName: number]: string }>(
    {},
  );
  /** 已选择的卡通图 */
  const [selectedOption, setSelectedOption] = useState<ImageSort>();
  /** 预览页状态 */
  const [previewType, setPreviewType] = useState<PreviewType>(
    PreviewType.Image,
  );
  /** 加价抽屉弹窗显影 */
  const [incrementDrawerVisible, setIncrementDrawerVisible] =
    useState<boolean>(false);
  /** 图片确认弹窗显影 */
  const [imageDrawerVisible, setImageDrawerVisible] = useState<boolean>(false);
  /** 分享弹窗显影 */
  const [shareVisible, setShareVisible] = useState<boolean>(false);
  /** vip弹窗显影 */
  const [vipIncrementVisible, setVipIncrementVisible] =
    useState<boolean>(false);
  /** 关联产品弹窗显影 */
  const [relatedProductVisible, setRelatedProductVisible] =
    useState<boolean>(false);
  /** 已经加价的价格 */
  const [addedPrice, setAddedPrice] = useState<number>(0);
  /** 是否已分享 */
  const [isShare, setIsShare] = useState<boolean>(false);
  /** 加车 loading */
  const [addCartLoading, setAddCartLoading] = useState<boolean>(false);
  /** 当前选中肤色 */
  const [selectedColor, setSelectedColor] = useState<Color>(
    colors.find((item) => item.name === selectedSkinColor)!,
  );
  /** 本地存储的图片 */
  const [localUserImages, setLocalUserImages] = useState<UserImage[]>(
    JSON.parse(window.localStorage.getItem(SaveLocalKey) || "[]") || [],
  );
  /** 圣诞包选择 */
  const [selectedBag, setSelectedBag] = useState<ChristmasBadData>();
  /** 显示的价格 */
  const displayPrice = parseFloat(price.slice(1)) + addedPrice;
  // 关联产品
  const relatedProduct = (increment?.relatedProduct || [])[0];
  /** 关联录音器产品弹窗显隐藏 */
  const [relatedRecordProductVisible, setRelatedRecordProductVisible] =
    useState<boolean>(false);
  /** 关联录音器产品加车状态 */
  const relatedRecordProductStatus = useRef<{
    addRecord: boolean;
    currentSize: string | undefined;
  }>({
    addRecord: false,
    currentSize: "",
  });
  /** 是否重选尺寸 */
  const [isResize, setIsResize] = useState<boolean>(false);
  /** 关联电子卡现隐 */
  const [relatedCardVisible, setRelatedCardVisible] = useState<boolean>(false);
  /** 是否加车电子卡 */
  const [isAddCard, setIsAddCard] = useState<boolean>(false);
  /** 模板ID对应的追销数据 */
  const _templateIdData = useRef<any>();
  /** 圣诞包本地数据 */
  const [christmasBagData, setChristmasBagData] = useState<
    ChristmasBag | undefined
  >(christmasBag);
  /** */
  const colorVariantsValue = useRef<ColorOption[]>([]);
  /** 加载分享背景图 */
  const loadShareImage = async () => {
    if (!shareBackgroundImage.current) {
      shareBackgroundImage.current = await loadBase64FromURL(
        "https://assets.sunzi.cool/new-mini-me/share.jpeg",
        1600,
        "jpeg",
        1,
      );
    }
  };

  /** 请求人脸分割 */
  const requestCutFace = async (image: string) => {
    gtag("event", "event_start_crop");
    const stime = new Date().getTime();
    // 压缩图片
    const compressImg = await compressImage(image, 800);
    const [response] = await Promise.all([
      myFaceCut(compressImg),
      loadShareImage(),
    ]);
    gtag("event", "event_end_crop");
    gtag("event", "timing_complete", {
      name: "event_crop_time",
      value: new Date().getTime() - stime,
    });
    if (response.status && response.data && response.data.length > 0) {
      responseAvatar.current = [];
      if (response.data.length > 1) {
        const avatar = response.data.map((item) => ({
          source: compressImg,
          image: item,
        }));
        responseAvatar.current = avatar;
      } else {
        const img = await loadBase64FromURL(response.data[0], 800, "png");
        const [faceRecognizeData, htmlImage] = await Promise.all([
          getFaceData(img),
          loadHTMLImage(img),
        ]);
        const { height, width } = htmlImage;
        const userImage: UserImage = {
          source: compressImg,
          image: response.data[0],
          height: height,
          width: width,
          userImage: image,
          faceRecognizeData: faceRecognizeData,
        };
        saveLocalImage(userImage);
        if (currentSide.current === 1 && images.length > 0) {
          setPathImage({});
        }
        images[currentSide.current - 1] = userImage;
        setImages(cloneDeep(images));
      }
      setCutState(true);
    } else {
      const userImage: UserImage = { source: compressImg };
      images[currentSide.current - 1] = userImage;
      setImages(cloneDeep(images));
      if (
        response.message === "Failed to fetch" ||
        response.message === "NETWORK ERROR"
      )
        // 网络错误
        setTaskStatus(TaskStatus.NetworkError);
      // 切图服务错误
      else setTaskStatus(TaskStatus.CropError);
    }
  };

  /** 重新请求人脸分割 */
  const reCutFace = () => {
    setTaskStatus(TaskStatus.Uploading);
    requestCutFace(images[currentSide.current - 1].source);
  };

  /** 图片合成生成列表 */
  const createImageList = () => {
    // 构建分类数据
    const all: { [propName: number]: ImageSort } = {};
    const allName = i18n.format("modules.mini.me.all");
    sortOption[allName] = [];
    miniMeData.forEach((item) => {
      const key = `${item.name}-${item.id}`;
      sortOption[key] = [];
      item.images.forEach((image) => {
        const imageData: ImageSort = {
          id: image.id,
          origin: image,
          isPathImage: image.avatar.path ? true : false,
          loading: {
            body: true,
          },
        };
        if (!all.hasOwnProperty(image.id)) {
          all[image.id] = imageData;
          image.annex?.map(
            (annex) => (imageData.loading[`annex-${annex.id}`] = true),
          );
          if (image.id === defaultOption) {
            sortOption[key].unshift(imageData);
            sortOption[allName].unshift(imageData);
          } else {
            sortOption[key].push(imageData);
            sortOption[allName].push(imageData);
          }
        } else {
          sortOption[key].push(all[image.id]);
        }
      });
    });
  };

  /** 重置列表选项 loading 状态 */
  const resetOptionLoading = () => {
    const allName = i18n.format("modules.mini.me.all");
    sortOption[allName].forEach((item) => {
      const isSameBodyUrl = item.origin.images.every(
        (value) => value.url === item.origin.images[0].url,
      );
      if (!isSameBodyUrl) {
        item.loading.body = true;
      }
      if (item.origin.annex) {
        const annexs = item.origin.annex;
        annexs.forEach((annex) => {
          const isSameAnnexUrl = annex.images.every(
            (value) => value.url === annex.images[0].url,
          );
          if (!isSameAnnexUrl) {
            item.loading[`annex-${annex.id}`] = true;
          }
        });
      }
    });
  };

  /** 生成带 path 的图片 */
  const createAvatarPath = async (data: MiniMeImage, avatar: fabric.Image) => {
    const element = document.createElement("canvas");
    const canvas = new fabric.Canvas(element);

    if (data.avatar.path) {
      const imageURL = data.images.find(
        (item) => item.color === selectedColor.name,
      )!.url;
      const bodyURL = thumbnail7n(imageURL, 300);
      const [path, body] = await Promise.all([
        loadSVGFromURL(data.avatar.path),
        loadImageFromURL(bodyURL),
      ]);
      canvas.setDimensions({
        width: 300,
        height: (data.height / data.width) * 300,
      });
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      // svg 路径
      const pathLeft = ((path.left ?? 0) / data.width) * canvasWidth;
      const pathTop = ((path.top ?? 0) / data.height) * canvasHeight;
      path
        .set({
          fill: "transparent",
          left: pathLeft,
          top: pathTop,
          originY: "top",
          originX: "left",
        })
        .scale(Math.min(canvasWidth / data.width, canvasHeight / data.height));
      // 身体
      body
        .set({
          left: canvas.getWidth() / 2,
          top: canvas.getHeight(),
          originY: "bottom",
          originX: "center",
        })
        .scale(
          Math.min(
            canvasWidth / (body.width ?? 0),
            canvasHeight / (body.height ?? 0),
          ),
        );
      canvas.add(body);
      // 头像
      const avatarWidth = (data.avatar.width / data.width) * canvasWidth;
      const avatarHeight = (data.avatar.height / data.height) * canvasHeight;
      let avatarLeft =
        ((data.avatar.left + data.avatar.width / 2) / data.width) * canvasWidth;
      let avatarTop =
        ((data.avatar.top + data.avatar.height) / data.height) * canvasHeight;
      let scale = Math.min(
        avatarWidth / (avatar.width ?? 0),
        avatarHeight / (avatar.height ?? 0),
      );
      // 人脸识别有数据就适配人脸
      if (images[0].faceRecognizeData && data.face) {
        // 获取人脸识别数据
        const { faceRect } = images[0].faceRecognizeData;
        const { face } = data;
        const faceWidth = (face.width / data.width) * canvasWidth;
        const faceHeight = (face.height / data.height) * canvasHeight;
        scale = Math.min(
          faceWidth / faceRect.width,
          faceHeight / faceRect.height,
        );
        const heightDistance =
          ((avatar.height as number) - faceRect.top - faceRect.height) * scale;
        avatarTop =
          ((face.top + face.height) / data.height) * canvasHeight +
          heightDistance;
        const avatarWidth = (avatar.width as number) * scale;
        avatarLeft =
          (face.left / data.width) * canvasWidth -
          faceRect.left * scale +
          avatarWidth / 2;
      }
      avatar
        .set({
          id: "avatar",
          left: avatarLeft,
          top: avatarTop,
          originY: "bottom",
          originX: "center",
          clipTo: (ctx: CanvasRenderingContext2D) => {
            ctx.save();
            path.set({
              objectCaching: false,
            });
            ctx.setTransform(
              window.devicePixelRatio,
              0,
              0,
              window.devicePixelRatio,
              0,
              0,
            );
            path.render(ctx);
            ctx.restore();
          },
        })
        .scale(scale);
      canvas.add(avatar);
      // 配件
      if (Array.isArray(data.annex) && data.annex.length > 0) {
        const annexsURL = data.annex.map((item) => {
          const url = item.images.find(
            (image) => image.color === selectedColor.name,
          )!.url;
          return thumbnail7n(url, 200);
        });
        const annexs = await Promise.all(
          annexsURL.map(async (item) => loadImageFromURL(item)),
        );

        annexs.forEach((item, index) => {
          const currentAnnex = (data.annex as AnnexData[])[index];
          // 如果人脸识别成功就适配配件
          if (images[0].faceRecognizeData) {
            // 获取人脸识别数据
            const { faceRect } = images[0].faceRecognizeData;
            const { face } = data;
            const { leftPoint, rightPoint, feature } = currentAnnex.faceFit;
            const avatarImageScale = avatar.scaleX as number;
            // 实际图片的脸区域的宽高
            const avatarFaceWidth = faceRect.width * avatarImageScale;
            // 脸部安全区大小
            const faceFitWidth = (face.width / data.width) * canvasWidth;
            // 脸部安全区坐标
            const faceFitLeft = (face.left / data.width) * canvasWidth;
            // 配件图片缩放比
            const scale = (item.width as number) / currentAnnex.width;
            // 拿到关键点坐标
            const keyPoint = getKeyPoint(images[0], feature);
            let annexTop;
            const annexScale =
              avatarFaceWidth / ((rightPoint.left - leftPoint.left) * scale);
            if (feature === "head") {
              annexTop =
                avatar._getLeftTopCoords().y +
                keyPoint.y * avatarImageScale -
                100 * avatarImageScale -
                leftPoint.top * scale * annexScale;
            } else {
              annexTop =
                avatar._getLeftTopCoords().y +
                keyPoint.y * avatarImageScale -
                leftPoint.top * scale * annexScale;
            }

            const faceLeft = faceFitLeft + (faceFitWidth - avatarFaceWidth) / 2;
            const annexLeft = faceLeft - leftPoint.left * scale * annexScale;
            item
              .set({
                originY: "center",
                originX: "center",
                left: annexLeft,
                top: annexTop,
              })
              .scale(annexScale);
          } else {
            const annexLeft =
              ((currentAnnex.left + currentAnnex.width / 2) / data.width) *
              canvasWidth;
            const annexTop =
              ((currentAnnex.top + currentAnnex.height / 2) / data.height) *
              canvasHeight;
            const annexWidth = (currentAnnex.width / data.width) * canvasWidth;
            const annexHeight =
              (currentAnnex.height / data.height) * canvasHeight;
            const annexScale = Math.min(
              annexWidth / (item.width ?? 0),
              annexHeight / (item.height ?? 0),
            );
            item
              .set({
                originY: "center",
                originX: "center",
                left: annexLeft,
                top: annexTop,
              })
              .scale(annexScale);
          }
          canvas.add(item);
        });
      }
    }
    canvas.renderAll();
    pathImage[data.id] = canvas.getElement().toDataURL();
    setPathImage(cloneDeep(pathImage));
    canvas.clear();
    canvas.dispose();
    element.width = element.height = 0;
  };

  /** 生成带 path 的图片 */
  const createPathImage = async () => {
    // 并行合成图片
    const _avatarObject = await loadImageFromURL(images[0].image as string);
    miniMeData
      .map((item) => item.images)
      .reduce((a, b) => a.concat(b))
      .map((item) => item.avatar.path && createAvatarPath(item, _avatarObject));
  };

  /** 关闭插件 */
  const handleClose = (force?: boolean) => {
    if (defaultImage && !force) {
      setCutState(false);
      _first.current = true;
      canvasData.current = [];
      setCurrentSide(1);
      setPreviewType(PreviewType.Image);
      setTaskStatus(TaskStatus.Uploading);
      setZuploaderVisible(true);
    } else {
      onClose();
      setStateInit();
    }
  };

  /** 数据初始化 */
  const setStateInit = () => {
    setCutState(false);
    setTaskStatus(TaskStatus.Uploading);
    canvasData.current = [];
    setCurrentSide(1);
    setPreviewType(PreviewType.Image);
    setSelectedOption(undefined);
    setBlurTipVisible(false);
    setPathImage({});
    setIsShare(false);
    setShareVisible(false);
    setVipIncrementVisible(false);
    setRelatedProductVisible(false);
    setRelatedRecordProductVisible(false);
    setEditVisible(false);
    setClipVisible(false);
    setIncrementDrawerVisible(false);
    setZuploaderVisible(true);
    setAddedPrice(0);
    setImages([]);
    setSelectedBag(undefined);
    relatedProductStatus.current = false;
    relatedRecordProductStatus.current = {
      addRecord: false,
      currentSize: "",
    };
    _first.current = true;
    incrementType.current = undefined;
  };

  /** 设置当前页 */
  const setCurrentSide = (side: number) => {
    prevSide.current = currentSide.current;
    currentSide.current = side;
  };

  /** 设置工厂数据 */
  const setFactoryData = (data: FactoryData) => (factoryData.current = data);

  /** 设置增值产品状态 */
  const setIncrementType = (type: IncrementType) =>
    (incrementType.current = type);

  /** 获取本地存储的图片 */
  const getLocalUserImage = () => {
    const stringData = window.localStorage.getItem(SaveLocalKey);
    if (stringData) {
      setLocalUserImages(JSON.parse(stringData));
    }
  };

  /** 清除本地存储的图片 */
  const clearLocalUserImage = (index?: number) => {
    if (index !== undefined) {
      // 如果有传下标就删除单个
      const data = JSON.parse(window.localStorage.getItem(SaveLocalKey)!);
      data.splice(index, 1);
      window.localStorage.setItem(SaveLocalKey, JSON.stringify(data));
      setLocalUserImages(data);
    } else {
      // 如果没有传下标就全清
      window.localStorage.removeItem(SaveLocalKey);
      setLocalUserImages([]);
    }
  };

  /** zuploader 关闭 */
  const handleZuploaderClose = () => {
    setZuploaderVisible(false);
    if (_first.current) {
      handleClose(true);
      setZuploaderVisible(true);
    }
  };

  /** zuploader 确定 */
  const handleZuploaderConfirm = (output: ZuploaderImage[]) => {
    if (_first.current) {
      _first.current = false;
    }
    if (incrementType.current === IncrementType.NewImageSide) {
      handleIncrement();
    }
    setTaskStatus(TaskStatus.Uploading);
    setCutState(false);
    setEditVisible(false);
    setBlurTipVisible(false);
    setIncrementDrawerVisible(false);
    const image = output[0].objectURL;
    requestCutFace(image);
  };

  /** 选择了本地图片 */
  const handleSelectedLocalImage = (userImage: UserImage) => {
    if (_first.current) {
      _first.current = false;
    }
    if (incrementType.current === IncrementType.NewImageSide) {
      handleIncrement();
    }
    images[currentSide.current - 1] = userImage;
    setImages(cloneDeep(images));
    /** 如果有模板id,跳过选择模板页，直接预览 */
    if (directToPreview()) {
    } else if (currentSide.current === 1) {
      setTaskStatus(TaskStatus.Choose);
      if (images.length > 0) {
        setPathImage({});
      }
    } else {
      setTaskStatus(TaskStatus.Preview);
    }
    setZuploaderVisible(false);
    setEditVisible(false);
    if (_choose.current) {
      setTimeout(() => _choose.current.update(), 0);
    }
  };

  /** 如果存在模板id，直接跳过选择页 */
  const directToPreview = () => {
    if (!defaultTemplateId) return false;
    let selectedName = i18n.format("modules.mini.me.all");
    const option = sortOption[selectedName].find(
      (option) => option.id == defaultTemplateId,
    );
    if (!option) return false;
    setSelectedOption(option);
    setTaskStatus(TaskStatus.Preview);
    return true;
  };

  /** 裁剪动画完成 */
  const handleFacutDone = () => {
    if (responseAvatar.current.length > 0) {
      setTaskStatus(TaskStatus.ChooseAvatar);
    } else {
      if (directToPreview()) {
        // 如果存在模板id，跳过选择页，直接预览
      } else if (currentSide.current === 1) {
        // 如果是第一面的话就是选择页面
        setTaskStatus(TaskStatus.Choose);
      } else {
        // 如果不是第一面就是渲染页面
        setTaskStatus(TaskStatus.Preview);
      }
      setBlurTipVisible(true);
    }
  };

  /** 手动裁剪完成 */
  const handleClipConfirm = async () => {
    const result = await _clip.current?.toClipURL();
    if (result) {
      const htmlImage = await loadHTMLImage(result);
      const { height, width } = htmlImage;
      const source = images[currentSide.current - 1].source;
      images[currentSide.current - 1] = {
        source: source,
        image: result,
        faceRecognizeData: undefined,
        height: height,
        width: width,
      };
      setImages(cloneDeep(images));
      if (editVisible) {
        // 如果上个页面是编辑页，则跳转到预览页面
        setEditVisible(false);
        setTaskStatus(TaskStatus.Preview);
      } else if (
        taskStatus === TaskStatus.CropError ||
        taskStatus === TaskStatus.NetworkError
      ) {
        // 如果上个页面是错误页，则跳转到选择页面
        setTaskStatus(TaskStatus.Choose);
      }
      setClipVisible(false);
    }
  };

  /** 模糊提示替换照片 */
  const handleBlurTipReplace = () => {
    setZuploaderVisible(true);
    gtag("event", "event_blur_replace");
  };

  /** 增值服务弹窗判断 */
  const handleIncrementDrawer = () => {
    _handleResetAddData();
    if (electricCard) {
      setRelatedCardVisible(true);
    } else if (relatedRecordProduct) {
      const isChooseMinSize = sizeVariant.some(
        (item) => item.checked && item.isMin,
      );
      setIsResize(isChooseMinSize);
      setRelatedRecordProductVisible(true);
    } else if (increment?.relatedProduct) {
      setRelatedProductVisible(true);
    } else if (shareLink) {
      setShareVisible(true);
    } else if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** 重置追销数据 */
  const _handleResetAddData = () => {
    setChristmasBagData(christmasBag);
    let arFilterResult = handleTemplateArDataFilter(
      canvasData.current[0].image?.id,
    );
    if (
      canvasData.current[0].image?.id != arFilterResult.id &&
      templateIdDoSkipAr
    )
      return;
    // 重置关联卡片
    if (templateIdElectricCard) {
      electricCard = templateIdElectricCard;
    } else {
      electricCard = _templateIdData.current.cardData.origin;
    }
    // 重置关联产品
    if (arFilterResult && templateIdDoSkipAr) {
      relatedRecordProduct = arFilterResult.ar;
    } else {
      relatedRecordProduct = _templateIdData.current.recordData.origin;
    }
    // 重置圣诞包
    if (templateIdChristmasBag) {
      christmasBag = templateIdChristmasBag;
      setChristmasBagData(templateIdChristmasBag);
    } else {
      christmasBag = _templateIdData.current.christmasBagData.origin;
    }
  };

  /** 根据模板ID过滤ar动画产品 */
  const handleTemplateArDataFilter: any = (id?: string | number) => {
    let filteredData;
    if (selectedOption?.origin.arImages) {
      const { arImages } = selectedOption.origin;
      const hadArImage = arImages?.find(
        (item) => item.color === selectedColor.name,
      );
      if (hadArImage) return false;
    }

    if (newTemplateArDatas) {
      newTemplateArDatas.forEach((item: any) => {
        if (item.id == id) {
          filteredData = item;
        }
      });
    }
    return filteredData ? filteredData : false;
  };

  /** 增值服务函数 */
  const handleIncrement = () => {
    switch (incrementType.current) {
      case IncrementType.SameSide: {
        gtag("event", "event_one_side_print");
        setIncrementDrawerVisible(false);
        handleIncrementDrawer();
        break;
      }
      case IncrementType.DoubleSide: {
        gtag("event", "event_double_side_print");
        setIncrementDrawerVisible(false);
        handleIncrementDrawer();
        break;
      }
      case IncrementType.NewImageSide: {
        gtag("event", "event_two_image_print");
        setIncrementDrawerVisible(false);
        setCurrentSide(currentSide.current + 1);
        setPreviewType(PreviewType.Image);
        break;
      }
      case IncrementType.LineText: {
        gtag("event", "event_add_text");
        setIncrementDrawerVisible(false);
        setCurrentSide(currentSide.current + 1);
        setPreviewType(PreviewType.Text);
        break;
      }
    }
  };

  /** 图片弹窗确认 */
  const handleImageDrawerConfirm = () => {
    setImageDrawerVisible(false);
    handleIncrementDrawer();
  };

  /** 分享弹窗确认 */
  const handleShareConfirm = () => {
    setShareVisible(false);
    if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** 分享弹窗关闭 */
  const handleShareClose = () => setShareVisible(false);

  /** 分享弹窗跳过 */
  const handleShareSkip = () => {
    setShareVisible(false);
    if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** vip 弹窗确认 */
  const handleVipIncrementConfirm = (increment?: VipIncrement) =>
    handleAddCart(factoryData.current!, increment);

  /** vip 弹窗关闭 */
  const handleVipIncrementClose = () => setVipIncrementVisible(false);

  /** 选择头像确认 */
  const handleChooseAvatarConfirm = (
    avatar: ResponseAvatar,
    htmlImage: HTMLImageElement,
    faceRecognizeData: UserImage["faceRecognizeData"] | undefined,
  ) => {
    const { height, width } = htmlImage;
    const userImage: UserImage = {
      source: avatar.source,
      image: avatar.image,
      height: height,
      width: width,
      userImage: avatar.source,
      faceRecognizeData: faceRecognizeData,
    };
    saveLocalImage(userImage);
    images[currentSide.current - 1] = userImage;
    setImages(cloneDeep(images));
    if (directToPreview()) {
    } else if (currentSide.current === 1) {
      setTaskStatus(TaskStatus.Choose);
    } else {
      setTaskStatus(TaskStatus.Preview);
    }
  };

  /** 选择头像关闭 */
  const handleChooseAvatarClose = () => {
    if (currentSide.current === 1) {
      handleClose();
    } else {
      setCurrentSide(currentSide.current - 1);
      setTaskStatus(TaskStatus.Preview);
    }
  };

  /** 关联产品弹窗确认 */
  const handleRelatedProductConfirm = (addStatus: boolean) => {
    setRelatedProductVisible(false);
    relatedProductStatus.current = addStatus;
    if (shareLink) {
      setShareVisible(true);
    } else if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** 关联录音器产品弹窗确认 */
  const handleRelatedRecordProductConfirm = (
    addStatus: boolean,
    currentSize?: string,
  ) => {
    setRelatedRecordProductVisible(false);
    relatedRecordProductStatus.current.addRecord = addStatus;
    relatedRecordProductStatus.current.currentSize = currentSize;
    if (increment?.relatedProduct) {
      setRelatedProductVisible(true);
    } else if (shareLink) {
      setShareVisible(true);
    } else if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** 关联电子卡产品弹窗确认 */
  const handleRelatedCardConfirm = (status: boolean) => {
    setIsAddCard(status);
    setRelatedCardVisible(false);
    if (relatedRecordProduct) {
      const isChooseMinSize = sizeVariant.some(
        (item) => item.checked && item.isMin,
      );
      setIsResize(isChooseMinSize);
      setRelatedRecordProductVisible(true);
    } else if (increment?.relatedProduct) {
      setRelatedProductVisible(true);
    } else if (shareLink) {
      setShareVisible(true);
    } else if (increment?.vip || christmasBag) {
      setVipIncrementVisible(true);
    } else {
      handleAddCart(factoryData.current!);
    }
  };

  /** 加车 */
  const handleAddCart = async (
    factoryData: FactoryData,
    vipIncrement?: VipIncrement,
  ) => {
    setAddCartLoading(true);
    try {
      gtag("event", "event_add_cart");
      // 获取token
      const token = await getToken7n();
      const encodeKey = `${uploadKey("mini-me")}`;
      const requestImages: any = {};
      const results: any = {};

      canvasData.current.map((item, index) => {
        if (item.image) {
          requestImages[index] = {
            avatar: item.image.avatar.image,
            compose: item.image.compose,
            originImage: item.image.avatar.originImage,
          };
        } else {
          requestImages[index] = {
            compose: item.lineText?.compose,
          };
        }
      });

      await Promise.all(
        Object.keys(requestImages).map(async (item, index) => {
          // 如果需要传头像和原始图片，则是卡通小人，否则是刻字
          if (requestImages[item].avatar && requestImages[item].originImage) {
            const origin = await compressImage(
              requestImages[item].originImage,
              800,
            );
            const [avatar, compose, originImage] = await Promise.all([
              upload7nB64({
                base64: requestImages[item].avatar,
                token,
                key: `${encodeKey}-facut-${index}.png`,
              }),
              upload7nB64({
                base64: requestImages[item].compose,
                token,
                key: `${encodeKey}-compose-${index}.png`,
              }),
              upload7nB64({
                base64: origin,
                token,
                key: `${encodeKey}-origin-${index}.png`,
              }),
            ]);
            results[item] = {
              avatar: host7n["prefix-na0"] + avatar.key,
              compose: host7n["prefix-na0"] + compose.key,
              originImage: host7n["prefix-na0"] + originImage.key,
            };
          } else {
            const compose = await upload7nB64({
              base64: requestImages[item].compose,
              token,
              key: `${encodeKey}-compose-${index}.png`,
            });
            results[item] = {
              compose: host7n["prefix-na0"] + compose.key,
            };
          }
        }),
      );
      canvasData.current.map((item, index) => {
        if (item.image?.avatar.image) {
          item.image.avatar.image = results[index].avatar;
          item.image.compose = results[index].compose;
          item.image.avatar.originImage = results[index].originImage;
        } else if (item.lineText) {
          item.lineText.compose = results[index].compose;
        }
      });
      // 如果有关联产品就添加
      let relatedProductIncrement = undefined;
      if (relatedProductStatus.current) {
        relatedProductIncrement = relatedProduct;
      }
      // 如果有关联录影器产品就添加
      let relatedRecordProductIncrement = undefined;
      if (relatedRecordProductStatus.current.addRecord) {
        relatedRecordProductIncrement =
          handleTemplateArDataFilter(canvasData.current[0].image?.id) &&
          templateIdDoSkipAr
            ? handleTemplateArDataFilter(canvasData.current[0].image?.id).ar
            : relatedRecordProduct;
        if (relatedRecordProductStatus.current.currentSize) {
          relatedRecordProductIncrement.currengSize =
            relatedRecordProductStatus.current.currentSize;
        }
      }
      onConfirm(
        {
          makeHeight: factoryData.makeHeight,
          makeWidth: factoryData.makeWidth,
          layoutHeight: factoryData.layoutHeight,
          layoutWidth: factoryData.layoutWidth,
          color: factoryData.color,
          bodyImage: factoryData.bodyImage,
          isBodyFlip: factoryData.isBodyFlip,
          data: canvasData.current,
        },
        incrementType.current as number,
        isShare,
        vipIncrement,
        relatedProductIncrement,
        selectedBag,
        relatedRecordProductIncrement,
        isAddCard && electricCard,
        colorVariantsValue.current,
      );
      setAddCartLoading(false);
      // 将其他数据恢复初始化设置
      setStateInit();
      gtag("event", "event_add_cart_finish");
    } catch (e) {
      setAddCartLoading(false);
    }
  };

  useEffect(() => {
    fabric.Object.prototype.objectCaching = false;
    createImageList();
    const stringData =
      JSON.parse(window.localStorage.getItem(SaveLocalKey) as string) || [];
    const userImages = stringData.map((item: any) => item.userImage);
    if (defaultImage && !userImages.includes(defaultImage)) {
      setTaskStatus(TaskStatus.Uploading);
      setZuploaderVisible(false);
      requestCutFace(defaultImage);
    }
    // 缓存追销数据
    _templateIdData.current = {
      recordData: {
        origin: relatedRecordProduct,
        current: newTemplateArDatas && newTemplateArDatas[0].ar,
      },
      cardData: {
        origin: electricCard,
        current: templateIdElectricCard,
      },
      christmasBagData: {
        origin: christmasBag,
        current: templateIdChristmasBag,
      },
    };
  }, []);

  useEffect(() => {
    const color = colors.find((item) => item.name === selectedSkinColor)!;
    setSelectedColor(color);
  }, [selectedSkinColor]);

  useEffect(() => {
    if (zuploaderVisible) {
      // 拿到本地存储的用户图片
      getLocalUserImage();
    }
  }, [zuploaderVisible]);

  /** 渲染页面 */
  const renderContent = () => {
    switch (taskStatus) {
      case TaskStatus.Uploading:
        return (
          <Loading.Facut cutState={cutState} doneCallback={handleFacutDone} />
        );
      case TaskStatus.CropError:
      case TaskStatus.NetworkError:
        return (
          <Error
            taskStatus={taskStatus}
            setZuploaderVisible={setZuploaderVisible}
            setClipVisible={setClipVisible}
            reCutFace={reCutFace}
            onClose={handleClose}
          />
        );
      case TaskStatus.ChooseAvatar:
        return (
          <ChooseAvatar
            avatars={responseAvatar.current}
            setZuploaderVisible={setZuploaderVisible}
            onConfirm={handleChooseAvatarConfirm}
            onClose={handleChooseAvatarClose}
          />
        );
      case TaskStatus.Choose:
        return (
          <Choose
            ref={_choose}
            userImage={images[0]}
            sortOption={sortOption}
            pathImage={pathImage}
            colors={colors}
            selectedColor={selectedColor}
            createPathImage={createPathImage}
            resetOptionLoading={resetOptionLoading}
            setSelectedColor={setSelectedColor}
            setSelectedOption={setSelectedOption}
            setTaskStatus={setTaskStatus}
            setZuploaderVisible={setZuploaderVisible}
            onClose={handleClose}
          />
        );
      case TaskStatus.Preview:
        return (
          <Preview
            shop={shop}
            title={title}
            price={price}
            images={images}
            previewType={previewType}
            miniMeImage={selectedOption!.origin}
            sideCount={sideCount}
            lineText={lineText}
            currentSide={currentSide}
            prevSide={prevSide}
            factoryData={factoryData.current}
            canvasData={canvasData}
            selectedColor={selectedColor}
            handleIncrementDrawer={handleIncrementDrawer}
            loadShareImage={loadShareImage}
            colorVariants={colorVariants}
            defaultTemplateId={defaultTemplateId}
            handleChangeIsFirst={() => {
              _first.current = true;
            }}
            setColorVariantsValue={(values) =>
              (colorVariantsValue.current = values)
            }
            setCurrentSide={setCurrentSide}
            setFactoryData={setFactoryData}
            setIncrementDrawerVisible={setIncrementDrawerVisible}
            setImageDrawerVisible={setImageDrawerVisible}
            setEditVisible={setEditVisible}
            setZuploaderVisible={setZuploaderVisible}
            setTaskStatus={setTaskStatus}
            setImages={setImages}
            setPreviewType={setPreviewType}
            gtag={gtag}
          />
        );
      default:
        return null;
    }
  };

  const fontInnerHTML = lineText?.fonts.map(
    (item) => `
    @font-face {
      font-weight: normal;
      font-style: normal;
      font-family: ${item.name};
      src: url('${item.url}');
    }
  `,
  );

  return (
    <div className="sunzi-mini-me-rebuild">
      <style>{fontInnerHTML}</style>
      {renderContent()}
      {zuploaderVisible && (
        <Wrapper className={styles.zuploaderWrapper}>
          <Zuploader
            local={zuploaderI18n[shop.language]}
            onClose={handleZuploaderClose}
            onConfirm={handleZuploaderConfirm}
            renderExtraContent={
              localUserImages.length > 0 && (
                <div className={styles.imageRecord}>
                  <div className={styles.imageRecordHeader}>
                    <div className={styles.title}>
                      {i18n.format("modules.mini.me.images.records")}
                    </div>
                    <div
                      onClick={() => clearLocalUserImage()}
                      className={styles.clear}
                    >
                      {i18n.format("modules.mini.me.clear.all")}
                    </div>
                  </div>
                  <div className={styles.imageRecordDetail}>
                    {i18n.format("modules.mini.me.images.records.detail")}
                  </div>
                  <div className={styles.imageRecordContent}>
                    {localUserImages.map((item, index) => (
                      <div key={index} className={styles.optionLayout}>
                        <div className={styles.background}>
                          <div
                            style={{ backgroundImage: `url(${item.image})` }}
                            className={styles.image}
                            onClick={() => handleSelectedLocalImage(item)}
                          />
                          <div
                            onClick={() => clearLocalUserImage(index)}
                            className={styles.clearIcon}
                          >
                            <Icon size={16} type="close-circle" color="#333" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }
          />
        </Wrapper>
      )}
      {editVisible && (
        <Wrapper className={styles.editWrapper}>
          <Edit
            images={images}
            setImages={setImages}
            setZuploaderVisible={setZuploaderVisible}
            setClipVisible={setClipVisible}
            onClose={() => setEditVisible(false)}
            onConfirm={() => setEditVisible(false)}
            gtag={gtag}
          />
        </Wrapper>
      )}
      {clipVisible && (
        <Wrapper className={styles.clipWrapper}>
          <Clip
            ref={_clip}
            source={images[currentSide.current - 1].source}
            gtag={gtag}
            theme="light"
            renderHeader={
              <Header
                closeIconType="left"
                closeIconColor="#000"
                onClose={() => setClipVisible(false)}
                renderContent={
                  <div
                    className={styles.clipHeaderContent}
                    onClick={() => {
                      _clip.current?.onGuideVisible(true)();
                    }}
                  >
                    <Icon type="question" size={30} />
                  </div>
                }
                renderSubmit={
                  <div
                    className={styles.clipSubmit}
                    onClick={handleClipConfirm}
                  >
                    <span>{i18n.format("modules.global.done")}</span>
                  </div>
                }
              />
            }
          />
        </Wrapper>
      )}
      {blurTipVisible && (
        <BlurTip
          source={images[currentSide.current - 1].image!}
          onClose={() => setBlurTipVisible(false)}
          onReplace={handleBlurTipReplace}
          gtag={gtag}
        />
      )}
      {incrementDrawerVisible && (
        <IncrementDrawer
          shop={shop}
          type={lineText ? "lineText" : selectedOption!.origin.type}
          image={canvasData.current[currentSide.current - 1].image!.compose}
          isOnlyText={isOnlyText}
          productIncrement={productIncrement}
          handleIncrement={handleIncrement}
          setIncrementType={setIncrementType}
          setAddedPrice={setAddedPrice}
          setZuploaderVisible={setZuploaderVisible}
          onClose={() => setIncrementDrawerVisible(false)}
        />
      )}
      {shareVisible && (
        <Share
          shop={shop}
          shareLink={shareLink}
          shareText={shareText}
          sharePrice={sharePrice}
          shareBackgroundImage={shareBackgroundImage.current}
          product={product}
          image={canvasData.current[0].image!.compose}
          gtag={gtag}
          price={displayPrice}
          setIsShare={setIsShare}
          onConfirm={handleShareConfirm}
          onClose={handleShareClose}
          onSkip={handleShareSkip}
        />
      )}
      {imageDrawerVisible && (
        <ImageDrawer
          images={canvasData.current.map((item) => item.image!.compose)}
          onClose={() => setImageDrawerVisible(false)}
          onConfirm={handleImageDrawerConfirm}
        />
      )}
      {vipIncrementVisible && (
        <Vip
          mask
          productContent={
            christmasBagData && (
              <div className={styles.giftBag}>
                <div className={styles.giftBagHeader}>
                  {i18n.format("modules.mini.me.gift.bag.title")}
                </div>
                <div className={styles.giftBagContent}>
                  <div className={styles.optionWrapper}>
                    <div
                      className={styles.option}
                      onClick={() => setSelectedBag(undefined)}
                    >
                      <div
                        className={classnames(styles.album, {
                          [styles.selected]: selectedBag === undefined,
                        })}
                      >
                        <div className={styles.noThank}>
                          {i18n.format("modules.mini.me.no.thank")}
                        </div>
                        <Icon
                          className={styles.selectedIcon}
                          type="selected"
                          size={40}
                        />
                      </div>
                    </div>
                  </div>
                  {christmasBagData.data.map(
                    (item, index) =>
                      item.price && (
                        <div key={index} className={styles.optionWrapper}>
                          <div
                            className={styles.option}
                            onClick={() => setSelectedBag(item)}
                          >
                            <div
                              className={classnames(styles.album, {
                                [styles.selected]: selectedBag === item,
                              })}
                              style={{
                                backgroundImage: `url(${item.album})`,
                              }}
                            >
                              <Icon
                                className={styles.selectedIcon}
                                type="selected"
                                size={40}
                              />
                            </div>
                            <div className={styles.price}>
                              {`+${shop.currencySymbol}${item.price}`}
                            </div>
                          </div>
                        </div>
                      ),
                  )}
                </div>
              </div>
            )
          }
          vipIncrement={increment?.vip}
          currencySymbol={shop.currencySymbol}
          onClose={handleVipIncrementClose}
          onConfirm={handleVipIncrementConfirm}
          gtag={gtag}
        />
      )}
      {relatedProductVisible && (
        <RelatedProduct
          shop={shop}
          relatedProduct={relatedProduct}
          onClose={() => setRelatedProductVisible(false)}
          onConfirm={handleRelatedProductConfirm}
        />
      )}
      {relatedRecordProductVisible && (
        <RelatedRecordProduct
          shop={shop}
          isArAnimation={Boolean(
            handleTemplateArDataFilter(canvasData.current[0].image?.id) &&
            templateIdDoSkipAr,
          )}
          isResize={
            Boolean(
              handleTemplateArDataFilter(canvasData.current[0].image?.id) &&
              templateIdDoSkipAr,
            )
              ? false
              : isResize
          }
          sizeVariant={sizeVariant.filter((item) => !item.isMin)}
          relatedRecordProduct={
            Boolean(
              handleTemplateArDataFilter(canvasData.current[0].image?.id) &&
              templateIdDoSkipAr,
            )
              ? handleTemplateArDataFilter(canvasData.current[0].image?.id).ar
              : relatedRecordProduct
          }
          onClose={() => setRelatedRecordProductVisible(false)}
          onConfirm={handleRelatedRecordProductConfirm}
        />
      )}
      {relatedCardVisible && electricCard && (
        <RelatedCard
          shop={shop}
          miniMeImage={canvasData.current[0].image!.compose}
          electricCard={
            Boolean(
              handleTemplateArDataFilter(canvasData.current[0].image?.id) &&
              templateIdDoSkipAr,
            ) && templateIdElectricCard
              ? templateIdElectricCard
              : electricCard
          }
          onClose={() => setRelatedCardVisible(false)}
          onConfirm={handleRelatedCardConfirm}
        />
      )}
      {addCartLoading && (
        <Wrapper className={styles.addLoadingWrapper}>
          <Loading.Line />
        </Wrapper>
      )}
    </div>
  );
};

export default MiniMe;
